import { useEffect, useRef, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  addNotification,
  setUnreadCount,
  markNotificationAsRead,
} from '@/features/notifications/notificationSlice'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { frameReceived } from '@/features/aiChat/frames'
import { createFrameBatcher } from '@/features/aiChat/frameBatcher'
import { setSocketConnected, useSocketConnected } from './socketStatus'
import { loadsInvalidated } from '@/store/freshness'

const MAX_RECONNECT_ATTEMPTS = 10
const BASE_DELAY_MS = 1000
const MAX_DELAY_MS = 30000

/**
 * Calculate exponential backoff delay.
 * Formula: min(BASE_DELAY * 2^attempt, MAX_DELAY)
 * @param {number} attempt - Zero-based attempt index
 * @returns {number} Delay in milliseconds
 */
function getBackoffDelay(attempt) {
  return Math.min(BASE_DELAY_MS * Math.pow(2, attempt), MAX_DELAY_MS)
}

/**
 * WebSocket Hook
 *
 * Manages WebSocket connection lifecycle and message handling.
 * Automatically connects when user is authenticated.
 * Uses exponential backoff for reconnection (max 10 attempts).
 *
 * @returns {Object} - { isConnected, isReconnecting, reconnectAttempt, maxAttemptsReached, send, disconnect, reconnect }
 */
export const useWebSocket = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  // The id, not the user object: profile, photo and /me refreshes replace
  // `state.auth.user`, and depending on the object tore the socket down while
  // it was still connecting (closing a CONNECTING socket fires `error`).
  const userId = useSelector((state) => state.auth.user?.id ?? null)
  const currentTenantId = useSelector((state) => state.auth.currentTenantId)
  const wsRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const reconnectAttemptRef = useRef(0)
  const intentionalDisconnectRef = useRef(false)
  const connectRef = useRef(null)
  // Published to socketStatus so the shell can read it without a second socket.
  const isConnected = useSocketConnected()
  const setIsConnected = useCallback((value) => setSocketConnected(value), [])
  // Assistant token frames are coalesced to one dispatch per animation frame.
  const aiFrameBatcherRef = useRef(null)
  if (!aiFrameBatcherRef.current) aiFrameBatcherRef.current = createFrameBatcher((frame) => dispatch(frameReceived(frame)))
  useEffect(() => () => aiFrameBatcherRef.current?.dispose(), [])
  const [isReconnecting, setIsReconnecting] = useState(false)
  const [reconnectAttempt, setReconnectAttempt] = useState(0)
  const [maxAttemptsReached, setMaxAttemptsReached] = useState(false)

  // Check if WebSocket is enabled (disabled by default in dev without ASGI server)
  const wsEnabled = import.meta.env.VITE_WS_ENABLED === 'true'

  // Determine WebSocket URL — use same origin so Vite proxy handles it
  const getWebSocketUrl = useCallback((path) => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    const tenantParam = currentTenantId ? `?tenant=${currentTenantId}` : ''
    return `${protocol}//${host}/ws/${path}${tenantParam}`
  }, [currentTenantId])

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((message) => {
    const { type, data } = message

    switch (type) {
      case 'notification':
        handleNotification(data)
        break

      case 'unread_count':
        handleUnreadCount(data)
        break

      case 'notification_read':
        handleNotificationRead(data)
        break

      case 'all_notifications_read':
        handleAllNotificationsRead(data)
        break

      case 'data_entry_created':
        handleDataEntryCreated(data)
        break

      case 'dashboard_update':
        handleDashboardUpdate(data)
        break

      case 'dashboard_created':
        handleDashboardCreated(data)
        break

      case 'widget_update':
        handleWidgetUpdate(data)
        break

      case 'pipeline_update':
        handlePipelineUpdate(data)
        break

      case 'ai_chat':
        aiFrameBatcherRef.current.push(data)
        break

      default:
        console.warn('WebSocket: Unknown message type', type)
    }
  }, [dispatch, queryClient])

  // Handle new notification
  const handleNotification = useCallback((notification) => {
    // Add to Redux store
    dispatch(addNotification(notification))

    // Show toast notification
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-surface shadow-overlay rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
        >
          <div className="flex-1 p-4">
            <div className="flex items-start">
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-ink">
                  {notification.title}
                </p>
                <p className="mt-1 text-sm text-ink-2">
                  {notification.message}
                </p>
              </div>
            </div>
          </div>
          <div className="flex border-l border-line">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-brand hover:text-brand"
            >
              Close
            </button>
          </div>
        </div>
      ),
      { duration: 5000 }
    )
  }, [dispatch])

  // Handle unread count update
  const handleUnreadCount = useCallback((data) => {
    dispatch(setUnreadCount(data.count))
  }, [dispatch])

  // Handle notification marked as read
  const handleNotificationRead = useCallback((data) => {
    dispatch(markNotificationAsRead(data.notification_id))
    dispatch(setUnreadCount(data.unread_count))
  }, [dispatch])

  // Handle all notifications marked as read
  const handleAllNotificationsRead = useCallback((data) => {
    dispatch(setUnreadCount(0))
    // Redux will handle marking all as read
  }, [dispatch])

  // Handle data entry created (invalidate widget data)
  const handleDataEntryCreated = useCallback((data) => {
    // Invalidate all widget queries to trigger refetch
    queryClient.invalidateQueries({ queryKey: ['widget-data'] })

    // Show success toast
    toast.success('Dashboard updated with new data', {
      duration: 3000,
    })
  }, [queryClient])

  // Handle dashboard update
  const handleDashboardUpdate = useCallback((data) => {
    // Invalidate dashboard query
    queryClient.invalidateQueries({ queryKey: ['dashboard', data.dashboard_id] })
  }, [queryClient])

  // A colleague created an organization-wide dashboard (the backend
  // broadcasts `dashboard_created` to the tenant, GRAPHIT-3C): forget the
  // recorded list loads so the next dashboards page visit reads the server,
  // and drop any query-cached list.
  const handleDashboardCreated = useCallback(() => {
    dispatch(loadsInvalidated())
    queryClient.invalidateQueries({ queryKey: ['dashboards'] })
  }, [dispatch, queryClient])

  // Handle widget update
  const handleWidgetUpdate = useCallback((data) => {
    const { action, widget_id, dashboard_id } = data

    if (action === 'created' || action === 'deleted') {
      // Refetch entire dashboard to get updated widget list
      queryClient.invalidateQueries({ queryKey: ['dashboard', dashboard_id] })
    } else if (action === 'updated') {
      // Invalidate specific widget data
      queryClient.invalidateQueries({ queryKey: ['widget-data', widget_id] })
    }
  }, [queryClient])

  // Handle pipeline sync progress
  const handlePipelineUpdate = useCallback((data) => {
    // Invalidate pipeline queries so the UI refreshes
    queryClient.invalidateQueries({ queryKey: ['pipelines'] })
    queryClient.invalidateQueries({ queryKey: ['sync-runs'] })

    // Dispatch custom event so any component can listen
    window.dispatchEvent(new CustomEvent('pipeline-update', { detail: data }))
  }, [queryClient])

  // Schedule a reconnection attempt with exponential backoff
  const scheduleReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) return

    const attempt = reconnectAttemptRef.current

    if (attempt >= MAX_RECONNECT_ATTEMPTS) {
      console.warn(`WebSocket: Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached`)
      setIsReconnecting(false)
      setMaxAttemptsReached(true)
      return
    }

    const delay = getBackoffDelay(attempt)
    console.log(`WebSocket: Reconnecting in ${delay}ms (attempt ${attempt + 1}/${MAX_RECONNECT_ATTEMPTS})`)

    setIsReconnecting(true)
    setMaxAttemptsReached(false)

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null
      reconnectAttemptRef.current += 1
      setReconnectAttempt(reconnectAttemptRef.current)
      connectRef.current?.()
    }, delay)
  }, [])

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!wsEnabled) {
      // WebSocket disabled in development (no ASGI server)
      return
    }

    if (!userId || !currentTenantId) {
      return
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return
    }

    // Close any existing socket that's in CONNECTING state
    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      wsRef.current.close()
    }

    try {
      const ws = new WebSocket(getWebSocketUrl('notifications/'))

      ws.onopen = () => {
        console.log('WebSocket: Connected')
        setIsConnected(true)
        setIsReconnecting(false)
        setMaxAttemptsReached(false)

        // Reset reconnect counter on successful connection
        reconnectAttemptRef.current = 0
        setReconnectAttempt(0)

        // Clear reconnect timeout if exists
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current)
          reconnectTimeoutRef.current = null
        }
      }

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          handleMessage(message)
        } catch (error) {
          console.error('WebSocket: Failed to parse message', error)
        }
      }

      ws.onclose = () => {
        // A socket this hook already replaced or closed on purpose must not
        // null out (or reconnect over) the live one: that is how a page ended
        // up holding several sockets and receiving every frame several times.
        if (wsRef.current !== ws) return
        setIsConnected(false)
        wsRef.current = null

        // Only attempt reconnection if the disconnect was not intentional.
        // A failure's `error` event carries no detail and is always followed
        // by this close, whose reconnect path logs each attempt.
        if (!intentionalDisconnectRef.current && userId) {
          scheduleReconnect()
        }
      }

      wsRef.current = ws
    } catch (error) {
      console.error('WebSocket: Connection failed', error)
      // Schedule reconnect on connection failure too
      if (!intentionalDisconnectRef.current && userId) {
        scheduleReconnect()
      }
    }
  }, [userId, currentTenantId, wsEnabled, getWebSocketUrl, handleMessage, scheduleReconnect, setIsConnected])

  // Keep ref in sync so scheduleReconnect always calls the latest connect
  connectRef.current = connect

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    intentionalDisconnectRef.current = true

    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    reconnectAttemptRef.current = 0
    setReconnectAttempt(0)
    setIsConnected(false)
    setIsReconnecting(false)
    setMaxAttemptsReached(false)
  }, [setIsConnected])

  // Manual reconnect — resets all state and starts fresh
  const reconnect = useCallback(() => {
    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    // Close existing socket
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }

    // Reset all reconnection state
    intentionalDisconnectRef.current = false
    reconnectAttemptRef.current = 0
    setReconnectAttempt(0)
    setIsReconnecting(false)
    setMaxAttemptsReached(false)

    // Connect immediately
    connect()
  }, [connect])

  // Send message to WebSocket
  const send = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message))
    } else {
      console.warn('WebSocket: Cannot send message, not connected')
    }
  }, [])

  // Connect on mount if user authenticated and tenant selected
  useEffect(() => {
    if (userId && currentTenantId) {
      intentionalDisconnectRef.current = false
      connect()
    }

    // Cleanup on unmount
    return () => {
      disconnect()
    }
  }, [userId, currentTenantId, connect, disconnect])

  return {
    isConnected,
    isReconnecting,
    reconnectAttempt,
    maxAttemptsReached,
    send,
    disconnect,
    reconnect,
  }
}
